package com.example.hukakmp.reportchat

import com.example.hukakmp.identification.GoogleAIBackend
import com.example.hukakmp.identification.getAI
import com.example.hukakmp.identification.getGenerativeModel
import com.example.hukakmp.identification.firebaseAiApp
import com.example.hukakmp.model.EspecieEntry
import com.example.hukakmp.model.ModalidadPesca
import com.example.hukakmp.util.fechaHoyDMY
import kotlinx.coroutines.await
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

actual val conversationalReportAvailable: Boolean = true

private const val MODEL_NAME = "gemini-3.8-flash"

private val reportJson = Json {
    ignoreUnknownKeys = true
    isLenient = true
}

@Serializable
private data class AiConversationalReport(
    val fecha: String? = null,
    val horaInicio: String? = null,
    val horaFin: String? = null,
    val modalidad: String? = null,
    val modalidadOtra: String? = null,
    val numeroCanas: Int? = null,
    val nombreLugar: String? = null,
    val zona: String? = null,
    val latitud: String? = null,
    val longitud: String? = null,
    val especies: List<AiConversationalSpecies> = emptyList(),
    val observaciones: String? = null,
    val sinCapturas: Boolean = false,
    val resumen: String = "",
)

@Serializable
private data class AiConversationalSpecies(
    val especie: String = "",
    val cantidad: Int = 1,
    val devueltos: Int = 0,
)

actual suspend fun extractConversationalReport(
    text: String,
): ConversationalReportResult {
    val clean = text.trim()
    if (clean.isBlank()) {
        return ConversationalReportResult.Error("Contame algo de tu jornada de pesca.")
    }

    return try {
        val backendOptions = js("{}")
        backendOptions.backend = GoogleAIBackend()
        val ai = getAI(firebaseAiApp(), backendOptions)

        val modelParams = js("{}")
        modelParams.model = MODEL_NAME
        val model = getGenerativeModel(ai, modelParams)

        val prompt = buildPrompt(clean)
        val result = model.generateContent(arrayOf<dynamic>(prompt)).await()
        val raw = result.response.text().trim()
        val cleaned = raw
            .removePrefix("```json")
            .removePrefix("```JSON")
            .removePrefix("```")
            .removeSuffix("```")
            .trim()

        val parsed = reportJson.decodeFromString<AiConversationalReport>(cleaned)
        val modality = parsed.modalidad
            ?.let { code -> ModalidadPesca.entries.firstOrNull { it.name == code } }

        ConversationalReportResult.Success(
            ConversationalReportDraft(
                fecha = parsed.fecha?.trim()?.takeIf(String::isNotBlank),
                horaInicio = parsed.horaInicio?.trim()?.takeIf(String::isNotBlank),
                horaFin = parsed.horaFin?.trim()?.takeIf(String::isNotBlank),
                modalidad = modality,
                modalidadOtra = parsed.modalidadOtra?.trim()?.takeIf(String::isNotBlank),
                numeroCanas = parsed.numeroCanas?.takeIf { it > 0 },
                nombreLugar = parsed.nombreLugar?.trim()?.takeIf(String::isNotBlank),
                zona = parsed.zona?.trim()?.takeIf(String::isNotBlank),
                latitud = parsed.latitud?.trim()?.takeIf(String::isNotBlank),
                longitud = parsed.longitud?.trim()?.takeIf(String::isNotBlank),
                especies = if (parsed.sinCapturas) {
                    emptyList()
                } else {
                    parsed.especies
                        .filter { it.especie.isNotBlank() && it.cantidad > 0 }
                        .map {
                            EspecieEntry(
                                especie = it.especie.trim(),
                                cantidad = it.cantidad.coerceAtLeast(1),
                                devueltos = it.devueltos.coerceIn(0, it.cantidad.coerceAtLeast(1)),
                            )
                        }
                },
                observaciones = parsed.observaciones?.trim()?.takeIf(String::isNotBlank),
                sinCapturas = parsed.sinCapturas,
                resumen = parsed.resumen.trim().ifBlank {
                    "Preparé los datos que pude reconocer. Revisalos antes de guardar el parte."
                },
            )
        )
    } catch (e: Throwable) {
        ConversationalReportResult.Error(
            "No pude interpretar la jornada. Probá nuevamente o contámela de otra forma."
        )
    }
}

private fun buildPrompt(userText: String): String = """
Sos el extractor de partes de pesca de Huka, una aplicación argentina de pesca recreativa.

HOY ES ${fechaHoyDMY()} según la fecha local de la aplicación.

Convertí el relato del pescador a datos estructurados para precargar un formulario.
No inventes datos que el usuario no dijo.

Reglas:
- fecha debe quedar en dd/MM/yyyy.
- Resolvé expresiones relativas como "hoy" o "ayer" usando la fecha indicada arriba.
- horaInicio y horaFin deben quedar HH:mm en formato 24 horas.
- modalidad SOLO puede ser uno de estos códigos:
  COSTA_LINEA
  EMBARCADO_LINEA
  SUBMARINA_COSTA
  SUBMARINA_EMBARCADO
  RED
  OTRA
- Si modalidad es OTRA, explicala en modalidadOtra.
- numeroCanas debe ser entero positivo o null.
- nombreLugar es el nombre textual del sitio mencionado.
- zona puede contener ciudad, provincia o referencia geográfica adicional.
- NO inventes latitud ni longitud. Solo completalas si el pescador escribió coordenadas explícitas.
- especies contiene nombre común, cantidad y devueltos.
- Si dice que no pescó nada, sinCapturas=true y especies=[].
- observaciones contiene datos útiles que no encajan en los otros campos.
- resumen debe ser una frase breve en español indicando qué entendiste.
- Si un dato no está presente o no puede inferirse con seguridad, usá null o lista vacía.

Respondé SOLAMENTE JSON válido:
{
  "fecha": "dd/MM/yyyy o null",
  "horaInicio": "HH:mm o null",
  "horaFin": "HH:mm o null",
  "modalidad": "COSTA_LINEA o null",
  "modalidadOtra": null,
  "numeroCanas": null,
  "nombreLugar": null,
  "zona": null,
  "latitud": null,
  "longitud": null,
  "especies": [
    {
      "especie": "Pejerrey",
      "cantidad": 3,
      "devueltos": 1
    }
  ],
  "observaciones": null,
  "sinCapturas": false,
  "resumen": "..."
}

Relato:
$userText
""".trimIndent()
